import {
  type ExternalPaymentSource,
  type StripeSource,
  type StripeSourceResponse,
  type TrialResponse,
  createVerificationAttempt,
  deactivateStripeSource,
  deserializeExternalPaymentSource,
  deserializeStripeSource,
  fetchBillingDetail,
  fetchExternalPaymentSources,
  fetchStripeSources,
  fetchTrials,
  finalizeAchSetupIntent,
  isBankAccount,
  isLegacyBankAccount,
  isModernBankAccount,
  isVerified,
  selectBillingDetail,
  updateDefaultPaymentMethod,
} from "@app/billing";
import { selectEnv } from "@app/config";
import { resetLoaderByIds } from "@app/loaders";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useCache,
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { billingAddBankAccountUrl, billingAddCreditCardUrl } from "@app/routes";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  ButtonLink,
  ButtonLinkExternal,
  FormGroup,
  Group,
  Input,
  Loading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
  ModalWrapper,
  Pill,
  TitleBar,
  Tooltip,
} from "../shared";

const ACH_REQUIRES_ORB = "ACH payment methods require Orb invoicing.";

type PaymentMethod =
  | { kind: "stripe"; source: StripeSource }
  | { kind: "external"; source: ExternalPaymentSource }
  | { kind: "trial"; source: TrialResponse };

const MS_IN_DAY = 1000 * 60 * 60 * 24;

function isActiveTrial(trial: TrialResponse): boolean {
  const now = Date.now();
  const begin = new Date(trial.range_begin).getTime();
  const end = new Date(trial.range_end).getTime();
  return now >= begin && now <= end;
}

function formatTrialDateRange(trial: TrialResponse): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const start = new Date(trial.range_begin).toLocaleDateString(
    "en-US",
    options,
  );
  const end = new Date(trial.range_end).toLocaleDateString("en-US", options);
  return `Free Trial: ${start} - ${end}`;
}

function getTrialDaysRemaining(trial: TrialResponse): number {
  const end = new Date(trial.range_end).getTime();
  return Math.max(0, Math.floor((end - Date.now()) / MS_IN_DAY));
}

function getDefaultPaymentMethod(
  paymentMethodUrl: string,
): PaymentMethod | null {
  const stripeMatch = paymentMethodUrl.match(/\/stripe_sources\/([^/]+)$/);
  if (stripeMatch?.[1]) {
    return {
      kind: "stripe",
      source: { id: stripeMatch[1] } as StripeSource,
    };
  }
  const externalMatch = paymentMethodUrl.match(
    /\/external_payment_sources\/([^/]+)$/,
  );
  if (externalMatch?.[1]) {
    return {
      kind: "external",
      source: { id: externalMatch[1] } as ExternalPaymentSource,
    };
  }
  const trialMatch = paymentMethodUrl.match(/\/trials\/([^/]+)$/);
  if (trialMatch?.[1]) {
    return {
      kind: "trial",
      source: { id: trialMatch[1] } as TrialResponse,
    };
  }
  return null;
}

function PaymentMethodCard({
  source,
  isDefault,
  onVerify,
  onRefreshModern,
  invoicedExternally,
  disabled,
  onMakeDefault,
  onDeactivate,
  isMakingDefault,
  isDeactivating,
  isRefreshing,
  actionInFlight,
}: {
  source: PaymentMethod;
  isDefault: boolean;
  onVerify: (source: StripeSource) => void;
  onRefreshModern: (source: StripeSource) => void;
  invoicedExternally: boolean;
  disabled: boolean;
  onMakeDefault: (source: PaymentMethod) => void;
  onDeactivate: (source: StripeSource) => void;
  isMakingDefault: boolean;
  isDeactivating: boolean;
  isRefreshing: boolean;
  actionInFlight: boolean;
}) {
  if (source.kind === "trial") {
    const canMakeDefault = !disabled && !actionInFlight && !isDefault;
    return (
      <Box key={source.source.id} size="md" className="mb-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-black">
              {formatTrialDateRange(source.source)}
            </h3>
            {isDefault ? (
              <Pill variant="default" data-testid="default-payment-method">
                Default
              </Pill>
            ) : null}
          </div>
          <ul className="text-sm text-black-500 space-y-1">
            <li>{getTrialDaysRemaining(source.source)} days remaining</li>
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => onMakeDefault(source)}
              disabled={!canMakeDefault}
              isLoading={isMakingDefault}
              data-testid={`make-default-payment-method-${source.source.id}`}
            >
              Make Default
            </Button>
          </div>
        </div>
      </Box>
    );
  }

  if (source.kind === "external") {
    const canMakeDefault = !disabled && !actionInFlight && !isDefault;
    return (
      <Box key={source.source.id} size="md" className="mb-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-black">
              {source.source.description}
            </h3>
            {isDefault ? (
              <Pill variant="default" data-testid="default-payment-method">
                Default
              </Pill>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => onMakeDefault(source)}
              disabled={!canMakeDefault}
              isLoading={isMakingDefault}
              data-testid={`make-default-payment-method-${source.source.id}`}
            >
              Make Default
            </Button>
          </div>
        </div>
      </Box>
    );
  }

  const stripeSource = source.source;
  const isStripeBankAccount = isBankAccount(stripeSource);
  const isLegacyAch = isLegacyBankAccount(stripeSource);
  const isModernAch = isModernBankAccount(stripeSource);
  const isStripeVerified = isVerified(stripeSource);
  const canMakeDefault =
    !disabled &&
    !actionInFlight &&
    !isDefault &&
    (!isStripeBankAccount || (isStripeVerified && invoicedExternally));
  const canDeactivate =
    !disabled &&
    !actionInFlight &&
    !isDefault &&
    stripeSource.stripeType !== "external";
  const achNeedsOrb =
    isStripeBankAccount && isStripeVerified && !invoicedExternally;
  const showLegacyVerify =
    isLegacyAch && !isStripeVerified && !disabled && !actionInFlight;
  const showModernVerify = isModernAch && !isStripeVerified && !disabled;
  const hostedVerificationUrl =
    stripeSource.stripeMetadata?.hostedVerificationUrl;
  const setupIntentId = stripeSource.stripeMetadata?.setupIntentId;
  const showRefresh = showModernVerify && Boolean(setupIntentId);
  const pendingHelp = hostedVerificationUrl
    ? "Finish verifying with your bank using Verify, then wait a minute and use Refresh status to update this payment method."
    : "After your bank finishes verification, use Refresh status to update this payment method.";
  const makeDefaultButton = (
    <Button
      variant="secondary"
      size="xs"
      onClick={() => onMakeDefault(source)}
      disabled={!canMakeDefault}
      isLoading={isMakingDefault}
      data-testid={`make-default-payment-method-${source.source.id}`}
    >
      Make Default
    </Button>
  );

  return (
    <Box key={source.source.id} size="md" className="mb-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-black">
            {stripeSource.description}
          </h3>
          {isDefault && (
            <Pill variant="default" data-testid="default-payment-method">
              Default
            </Pill>
          )}
          {isStripeBankAccount && (
            <Pill
              variant={isStripeVerified ? "success" : "error"}
              data-testid={
                isStripeVerified
                  ? "bank-account-verified"
                  : "bank-account-not-verified"
              }
            >
              {isStripeVerified ? "Verified" : "Not verified"}
            </Pill>
          )}
        </div>

        <ul className="text-sm text-black-500 space-y-1">
          {stripeSource.stripeMetadata?.name ? (
            <li data-testid="stripe-source-name">
              <span className="font-medium">Cardholder Name:</span>{" "}
              {stripeSource.stripeMetadata.name}
            </li>
          ) : null}
          {stripeSource.stripeMetadata?.bankName ? (
            <li data-testid="stripe-source-bank-name">
              <span className="font-medium">Bank Name:</span>{" "}
              {stripeSource.stripeMetadata.bankName}
            </li>
          ) : null}
          {stripeSource.stripeMetadata?.last4 ? (
            <li data-testid="stripe-source-last4">
              <span className="font-medium">Last 4 Digits:</span>{" "}
              {stripeSource.stripeMetadata.last4}
            </li>
          ) : null}
          {stripeSource.stripeMetadata?.expMonth != null &&
          stripeSource.stripeMetadata?.expYear != null ? (
            <li
              data-testid={`stripe-source-exp-${stripeSource.stripeMetadata.expMonth}-${stripeSource.stripeMetadata.expYear}`}
            >
              <span className="font-medium">Expiration:</span>{" "}
              {stripeSource.stripeMetadata.expMonth}/
              {stripeSource.stripeMetadata.expYear}
            </li>
          ) : null}
          {stripeSource.stripeMetadata?.status ? (
            <li data-testid="stripe-source-status">
              <span className="font-medium">Status:</span>{" "}
              {stripeSource.stripeMetadata.status}
            </li>
          ) : null}
          {showModernVerify ? (
            <li data-testid="modern-ach-pending-help">{pendingHelp}</li>
          ) : null}
        </ul>

        <div className="flex flex-wrap gap-2">
          {showLegacyVerify && (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => onVerify(stripeSource)}
              data-testid={`verify-bank-account-${source.source.id}`}
            >
              Verify
            </Button>
          )}
          {showModernVerify && hostedVerificationUrl ? (
            <ButtonLinkExternal
              href={hostedVerificationUrl}
              variant="secondary"
              size="xs"
              data-testid={`verify-bank-account-${source.source.id}`}
            >
              Verify
            </ButtonLinkExternal>
          ) : null}
          {showRefresh ? (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => onRefreshModern(stripeSource)}
              disabled={actionInFlight}
              isLoading={isRefreshing}
              data-testid={`refresh-bank-account-${source.source.id}`}
            >
              Refresh status
            </Button>
          ) : null}
          {achNeedsOrb ? (
            <Tooltip text={ACH_REQUIRES_ORB}>{makeDefaultButton}</Tooltip>
          ) : (
            makeDefaultButton
          )}
          <Button
            variant="secondary"
            size="xs"
            onClick={() => onDeactivate(stripeSource)}
            disabled={!canDeactivate}
            isLoading={isDeactivating}
            data-testid={`deactivate-payment-method-${source.source.id}`}
          >
            Delete
          </Button>
        </div>
      </div>
    </Box>
  );
}

function VerifyBankAccountModal({
  source,
  isOpen,
  onClose,
  onSuccess,
}: {
  source: StripeSource | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const dispatch = useDispatch();
  const [amount1, setAmount1] = useState("");
  const [amount2, setAmount2] = useState("");
  const [error, setError] = useState("");
  const loader = useLoader(createVerificationAttempt);

  useEffect(() => {
    if (isOpen) {
      dispatch(resetLoaderByIds({ ids: [`${createVerificationAttempt}`] }));
      setAmount1("");
      setAmount2("");
      setError("");
    }
  }, [dispatch, isOpen]);

  useLoaderSuccess(loader, () => {
    onSuccess();
    onClose();
    setAmount1("");
    setAmount2("");
    setError("");
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!amount1.trim() || !amount2.trim()) {
      setError("Both deposit amounts are required");
      return;
    }
    const parsedAmount1 = Number.parseFloat(amount1);
    const parsedAmount2 = Number.parseFloat(amount2);
    if (
      Number.isNaN(parsedAmount1) ||
      Number.isNaN(parsedAmount2) ||
      parsedAmount1 <= 0 ||
      parsedAmount2 <= 0
    ) {
      setError("Please enter valid positive deposit amounts");
      return;
    }
    if (!source) return;
    dispatch(
      createVerificationAttempt({
        stripeSourceId: source.id,
        amountDecimal1: parsedAmount1.toFixed(2),
        amountDecimal2: parsedAmount2.toFixed(2),
      }),
    );
  };

  if (!source) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} className="min-w-[400px]">
        <ModalHeading
          title="Verify bank account"
          description={source.description}
          onClose={onClose}
        />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && (
              <Banner variant="error" className="mb-4">
                {error}
              </Banner>
            )}
            <BannerMessages {...loader} />
            <Banner variant="info" className="mb-4 text-sm">
              <div className="space-y-2">
                <p>
                  New bank accounts added for ACH payments need to be verified.
                  Verification is done via two small deposits into your bank
                  account. These deposits will take 1-2 business days to appear.
                  The statement description for these deposits will be AMNTS:
                  and then the values of the two microdeposits that were sent.
                </p>
                <p>
                  Wait for the deposits to appear on your statement. Then, enter
                  the two amounts here to complete verification. The ordering of
                  the amounts does not matter.
                </p>
              </div>
            </Banner>
            <FormGroup
              label="First deposit amount in dollars (e.g. 0.45)"
              htmlFor="amount1"
            >
              <Input
                id="amount1"
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                value={amount1}
                onChange={(e) => setAmount1(e.target.value)}
                placeholder="0.00"
              />
            </FormGroup>
            <FormGroup
              label="Second deposit amount in dollars (e.g. 0.32)"
              htmlFor="amount2"
            >
              <Input
                id="amount2"
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                value={amount2}
                onChange={(e) => setAmount2(e.target.value)}
                placeholder="0.00"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter
            actions={[
              <Button
                key="cancel"
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                Cancel
              </Button>,
              <Button key="verify" type="submit" isLoading={loader.isLoading}>
                Verify & Exit
              </Button>,
            ]}
          />
        </form>
      </Modal>
    </ModalWrapper>
  );
}

export const BillingPaymentMethodsPage = () => {
  const org = useSelector(selectOrganizationSelected);
  const env = useSelector(selectEnv);
  const billingDetail = useSelector(selectBillingDetail);
  const billingDetailId = org.billingDetailId || org.id;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  useQuery(fetchBillingDetail({ id: billingDetailId }));
  const stripeSourcesCache = useCache(
    fetchStripeSources({ id: billingDetailId }),
  );
  const externalSourcesCache = useCache(
    fetchExternalPaymentSources({ id: billingDetailId }),
  );
  const trialsCache = useCache(fetchTrials({ id: billingDetailId }));

  const [verifySource, setVerifySource] = useState<StripeSource | null>(null);
  const [activeAction, setActiveAction] = useState<{
    kind: "make-default" | "deactivate" | "refresh";
    sourceId: string;
  } | null>(null);
  const [pendingDeactivateIds, setPendingDeactivateIds] = useState<string[]>(
    [],
  );
  const [lastDeactivateAttemptId, setLastDeactivateAttemptId] = useState<
    string | null
  >(null);

  const makeDefaultLoader = useLoader(updateDefaultPaymentMethod);
  const deactivateLoader = useLoader(deactivateStripeSource);
  const finalizeLoader = useLoader(finalizeAchSetupIntent);
  const actionInFlight =
    makeDefaultLoader.isLoading ||
    deactivateLoader.isLoading ||
    finalizeLoader.isLoading;
  useLoaderSuccess(makeDefaultLoader, () => {
    setActiveAction(null);
    stripeSourcesCache.trigger();
    externalSourcesCache.trigger();
    trialsCache.trigger();
  });
  useLoaderSuccess(deactivateLoader, () => {
    setLastDeactivateAttemptId(null);
    setActiveAction(null);
    stripeSourcesCache.trigger();
    externalSourcesCache.trigger();
    trialsCache.trigger();
  });
  useLoaderSuccess(finalizeLoader, () => {
    setActiveAction(null);
    stripeSourcesCache.trigger();
  });
  useEffect(() => {
    if (deactivateLoader.isError && lastDeactivateAttemptId) {
      setPendingDeactivateIds((prev) =>
        prev.filter((id) => id !== lastDeactivateAttemptId),
      );
      setLastDeactivateAttemptId(null);
      setActiveAction(null);
    }
  }, [deactivateLoader.isError, lastDeactivateAttemptId]);

  const handleMakeDefault = (paymentMethod: PaymentMethod) => {
    if (actionInFlight) {
      return;
    }
    const paymentMethodUrl =
      paymentMethod.kind === "stripe"
        ? `${env.billingUrl}/stripe_sources/${paymentMethod.source.id}`
        : paymentMethod.kind === "external"
          ? `${env.billingUrl}/external_payment_sources/${paymentMethod.source.id}`
          : `${env.billingUrl}/trials/${paymentMethod.source.id}`;
    setActiveAction({
      kind: "make-default",
      sourceId: paymentMethod.source.id,
    });
    dispatch(
      updateDefaultPaymentMethod({
        billingDetailId,
        paymentMethodUrl,
      }),
    );
  };

  const handleDeactivate = (source: StripeSource) => {
    if (actionInFlight) {
      return;
    }
    const confirmed = window.confirm(
      `Delete payment method "${source.description}"? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }
    setLastDeactivateAttemptId(source.id);
    setPendingDeactivateIds((prev) => [...prev, source.id]);
    setActiveAction({ kind: "deactivate", sourceId: source.id });
    dispatch(deactivateStripeSource({ id: source.id }));
  };

  const handleRefreshModern = (source: StripeSource) => {
    const setupIntentId = source.stripeMetadata?.setupIntentId;
    if (!setupIntentId || actionInFlight) {
      return;
    }
    setActiveAction({ kind: "refresh", sourceId: source.id });
    dispatch(
      finalizeAchSetupIntent({
        id: billingDetailId,
        setupIntentId,
      }),
    );
  };

  const rawActiveSources: StripeSource[] = useMemo(
    () =>
      (stripeSourcesCache.data?._embedded?.stripe_sources || [])
        .filter((r: StripeSourceResponse) => r.deactivated_at === null)
        .map(deserializeStripeSource),
    [stripeSourcesCache.data],
  );
  const activeSources: StripeSource[] = useMemo(
    () =>
      rawActiveSources.filter(
        (source) => !pendingDeactivateIds.includes(source.id),
      ),
    [pendingDeactivateIds, rawActiveSources],
  );
  const externalSources: ExternalPaymentSource[] = (
    externalSourcesCache.data?._embedded?.external_payment_sources || []
  ).map(deserializeExternalPaymentSource);
  const activeTrials: TrialResponse[] = (
    trialsCache.data?._embedded?.trials || []
  ).filter(isActiveTrial);
  const paymentMethods: PaymentMethod[] = [
    ...activeSources.map((source) => ({ kind: "stripe" as const, source })),
    ...externalSources.map((source) => ({ kind: "external" as const, source })),
    ...activeTrials.map((source) => ({ kind: "trial" as const, source })),
  ];
  const defaultPaymentMethod = getDefaultPaymentMethod(
    billingDetail.paymentMethodUrl,
  );
  const isLoading =
    (!stripeSourcesCache.data && !stripeSourcesCache.lastSuccess) ||
    (!externalSourcesCache.data && !externalSourcesCache.lastSuccess) ||
    (!trialsCache.data && !trialsCache.lastSuccess);

  useEffect(() => {
    // Always revalidate after navigating back from add card/bank account pages.
    // Stripe may land here with setup_intent after a redirect; finalize that
    // enrollment instead of clearing the loader.
    const setupIntentId = searchParams.get("setup_intent") || "";
    if (setupIntentId !== "") {
      dispatch(finalizeAchSetupIntent({ id: billingDetailId, setupIntentId }));
      setSearchParams({}, { replace: true });
    } else {
      dispatch(resetLoaderByIds({ ids: [`${finalizeAchSetupIntent}`] }));
    }
    stripeSourcesCache.trigger();
    externalSourcesCache.trigger();
    trialsCache.trigger();
  }, [billingDetailId]);

  useEffect(() => {
    setPendingDeactivateIds((prev) => {
      const next = prev.filter((id) =>
        rawActiveSources.some((source) => source.id === id),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [rawActiveSources]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  return (
    <Group>
      <TitleBar description="Manage your payment methods">
        Payment Methods
      </TitleBar>
      <BannerMessages {...makeDefaultLoader} />
      <BannerMessages {...deactivateLoader} />
      <BannerMessages {...finalizeLoader} />

      {paymentMethods.length > 0 ? (
        <div className="space-y-2">
          {paymentMethods.map((source) => (
            <PaymentMethodCard
              key={`${source.kind}-${source.source.id}`}
              source={source}
              isDefault={
                source.source.id === defaultPaymentMethod?.source.id &&
                source.kind === defaultPaymentMethod.kind
              }
              onVerify={setVerifySource}
              onRefreshModern={handleRefreshModern}
              invoicedExternally={billingDetail.invoicedExternally}
              disabled={isLoading}
              onMakeDefault={handleMakeDefault}
              onDeactivate={handleDeactivate}
              isMakingDefault={
                makeDefaultLoader.isLoading &&
                activeAction?.kind === "make-default" &&
                activeAction.sourceId === source.source.id
              }
              isDeactivating={
                deactivateLoader.isLoading &&
                activeAction?.kind === "deactivate" &&
                activeAction.sourceId === source.source.id
              }
              isRefreshing={
                finalizeLoader.isLoading &&
                activeAction?.kind === "refresh" &&
                activeAction.sourceId === source.source.id
              }
              actionInFlight={actionInFlight}
            />
          ))}
        </div>
      ) : (
        <p className="text-black-500 py-4">
          You have no configured payment methods.
        </p>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        <ButtonLink to={billingAddCreditCardUrl()} variant="primary">
          Add Credit Card
        </ButtonLink>
        {billingDetail.invoicedExternally ? (
          <ButtonLink to={billingAddBankAccountUrl()} variant="secondary">
            Add Bank Account
          </ButtonLink>
        ) : null}
      </div>

      <VerifyBankAccountModal
        source={verifySource}
        isOpen={verifySource !== null}
        onClose={() => setVerifySource(null)}
        onSuccess={() => {
          stripeSourcesCache.trigger();
        }}
      />
    </Group>
  );
};
